import axios from 'axios';
import qs from 'qs';
import { loginUserSuccess, tokenExpired } from '../redux/auth/actions';
import { history, store } from '../redux';
import errorHandler from '../helpers/errorHandler';

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  //baseURL: "http://localhost:8080/wzap",
});

const REFRESH_URL = '/oauth/token';
const CLIENT_ID = 'client';
const CLIENT_SECRET = '123';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
      return;
    }

    promise.resolve(token);
  });

  failedQueue = [];
};

const getStoredAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem('authUser') || 'null');
  } catch {
    return null;
  }
};

const isAuthTry = (url = '', method = '', data) => {
  const isTokenEndpoint = url.includes(REFRESH_URL);
  const isPost = method.toLowerCase() === 'post';

  if (!isTokenEndpoint || !isPost) {
    return false;
  }

  if (typeof data === 'string') {
    return data.includes('grant_type=password');
  }

  return data?.grant_type === 'password';
};

const updateStoredAuth = ({ accessToken, refreshToken }) => {
  const authUserRaw = localStorage.getItem('authUser');

  if (!authUserRaw) {
    return;
  }

  try {
    const authUser = JSON.parse(authUserRaw);
    const updatedAuthUser = {
      ...authUser,
      token: accessToken,
      access_token: accessToken,
      refresh_token: refreshToken || authUser.refresh_token,
      data: authUser?.data
        ? {
            ...authUser.data,
            token: accessToken,
          }
        : authUser.data,
    };

    localStorage.setItem('authUser', JSON.stringify(updatedAuthUser));
  } catch (storageError) {
    console.log('[Auth-Refresh] Falha ao atualizar authUser no localStorage.', storageError);
  }
};

const clearAuthSession = dispatch => {
  localStorage.removeItem('authUser');
  localStorage.removeItem('refreshToken');
  delete api.defaults.headers.Authorization;
  dispatch(tokenExpired(history));
};

const refreshAccessToken = async dispatch => {
  const refreshToken =
    localStorage.getItem('refreshToken') ||
    store.getState()?.auth?.user?.refresh_token ||
    getStoredAuthUser()?.refresh_token;

  if (!refreshToken) {
    throw new Error('Refresh token not found');
  }

  const basicAuth = `Basic ${btoa(`${CLIENT_ID}:${CLIENT_SECRET}`)}`;
  const data = qs.stringify({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
  });

  console.log('[Auth-Refresh] Iniciando chamada de refresh token.');

  const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}${REFRESH_URL}`, data, {
    headers: {
      Authorization: basicAuth,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });

  const { access_token: newAccessToken, refresh_token: newRefreshToken } = response.data;

  console.log('[Auth-Refresh] Refresh concluido com sucesso. Novo access token:', newAccessToken);

  api.defaults.headers.Authorization = `Bearer ${newAccessToken}`;
  dispatch(loginUserSuccess(newAccessToken));

  localStorage.setItem('refreshToken', newRefreshToken || refreshToken);
  updateStoredAuth({
    accessToken: newAccessToken,
    refreshToken: newRefreshToken || refreshToken,
  });

  return newAccessToken;
};

api.interceptors.response.use(
  response => {
    const requestUrl = response?.config?.url || '';
    const requestMethod = response?.config?.method || '';
    const isTokenRequest = requestUrl.includes(REFRESH_URL) && requestMethod.toLowerCase() === 'post';
    const refreshToken = response?.data?.refresh_token;

    if (isTokenRequest && refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }

    return response;
  },
  async error => {
    const { dispatch } = store;
    const { config } = error;
    const status = error.response ? error.response.status : null;
    const { url = '', method = '', data } = config || {};
    const isLoginRequest = isAuthTry(url, method, data);
    const isRefreshRequest = url.includes(REFRESH_URL) && method.toLowerCase() === 'post' && !isLoginRequest;

    if (status === 401 && config && !config._retry && !isLoginRequest && !isRefreshRequest) {
      console.log('[Auth-Refresh] Access token expirado detectado na URL:', url);

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            console.log('[Auth-Refresh] Reprocessando requisicao da fila com o novo token:', url);
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
            return api(config);
          })
          .catch(queueError => Promise.reject(queueError));
      }

      config._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await refreshAccessToken(dispatch);
        processQueue(null, newAccessToken);
        console.log('[Auth-Refresh] Reprocessando requisicoes aguardando na fila.');
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(config);
      } catch (refreshError) {
        processQueue(refreshError, null);
        console.log('[Auth-Refresh] Falha no refresh token. Usuario sera deslogado.', refreshError);
        clearAuthSession(dispatch);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (status === 401 && isRefreshRequest) {
      console.log('[Auth-Refresh] Refresh token retornou 401. Usuario sera deslogado.');
      clearAuthSession(dispatch);
      return Promise.reject(error);
    }

    let message = errorHandler(error);
    return Promise.reject(message);
  }
);

export default api;
